export const SHOP_PERMISSIONS = {
  PROFILE_READ: 'shop.profile.read',
  PROFILE_MANAGE: 'shop.profile.manage',
  SETTINGS_READ: 'shop.settings.read',
  SETTINGS_MANAGE: 'shop.settings.manage',
  MEMBERS_READ: 'shop.members.read',
  MEMBERS_INVITE: 'shop.members.invite',
  MEMBERS_MANAGE: 'shop.members.manage',
  MEMBERS_ROLE_MANAGE: 'shop.members.role.manage',
  MEMBERS_PERMISSIONS_MANAGE: 'shop.members.permissions.manage',
  MEMBERS_STATUS_MANAGE: 'shop.members.status.manage',
  APPOINTMENTS_READ: 'appointments.read',
  APPOINTMENTS_READ_OWN: 'appointments.read_own',
  APPOINTMENTS_READ_ALL: 'appointments.read_all',
  APPOINTMENTS_CREATE: 'appointments.create',
  APPOINTMENTS_UPDATE: 'appointments.update',
  APPOINTMENTS_CHECK_IN: 'appointments.check_in',
  APPOINTMENTS_MANAGE_OWN: 'appointments.manage_own',
  APPOINTMENTS_MANAGE_ALL: 'appointments.manage_all',
  APPOINTMENTS_CANCEL: 'appointments.cancel',
  APPOINTMENT_SETTINGS_MANAGE: 'shop.appointment_settings.manage',
  CLIENTS_READ: 'clients.read',
  CLIENTS_READ_ASSIGNED: 'clients.read_assigned',
  CLIENTS_READ_ALL: 'clients.read_all',
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_UPDATE: 'clients.update',
  CLIENTS_DELETE: 'clients.delete',
  CLIENTS_MERGE: 'clients.merge',
  CLIENTS_CONTACT_READ: 'clients.contact.read',
  CLIENTS_CONTACT_MANAGE: 'clients.contact.manage',
  CLIENTS_NOTES_READ: 'clients.notes.read',
  CLIENTS_NOTES_MANAGE: 'clients.notes.manage',
  MEMBERS_ADD: 'members.add',
  MEMBERS_UPDATE: 'members.update',
  MEMBERS_REMOVE: 'members.remove',
  SERVICES_READ: 'services.read',
  SERVICES_CREATE: 'services.create',
  SERVICES_UPDATE: 'services.update',
  SERVICES_MANAGE: 'services.manage',
  SERVICES_DELETE: 'services.delete',
  PAYMENTS_READ: 'payments.read',
  PAYMENTS_CREATE: 'payments.create',
  PAYMENTS_REFUND: 'payments.refund',
  SHOP_READ: 'shop.read',
  SHOP_UPDATE: 'shop.update',
  SHOP_ONBOARDING_COMPLETE: 'shop.onboarding.complete',
  SHOP_PROFILE_READ: 'shop.profile.read',
  SHOP_PROFILE_MANAGE: 'shop.profile.manage',
  SHOP_PROFILE_SLUG_MANAGE: 'shop.profile.slug.manage',
  SHOP_DELETE: 'shop.delete',
  SHOP_OWNERSHIP_TRANSFER: 'shop.ownership.transfer',
  SHOP_ARCHIVE: 'shop.archive',
  SHOP_DELETE_REQUEST: 'shop.delete_request',
  SHOP_ADMINS_MANAGE: 'shop.admins.manage',
  SHOP_MEMBERS_READ: 'shop.members.read',
  SHOP_MEMBERS_ADD: 'shop.members.add',
  SHOP_MEMBERS_MANAGE: 'shop.members.manage',
  SHOP_SETTINGS_MANAGE: 'shop.settings.manage',
  SHOP_NOTIFICATION_SETTINGS_READ: 'shop.notification_settings.read',
  SHOP_NOTIFICATION_SETTINGS_MANAGE: 'shop.notification_settings.manage',
  SHOP_MEMBER_SETTINGS_READ: 'shop.member_settings.read',
  SHOP_MEMBER_SETTINGS_MANAGE: 'shop.member_settings.manage',
  SHOP_CLIENT_SETTINGS_MANAGE: 'shop.client_settings.manage',
  SCHEDULE_CLOSURES_MANAGE: 'shop.schedule.closures.manage',
  SHOP_BILLING_MANAGE: 'shop.billing.manage',
  REPORTS_VIEW: 'reports.view',
  SCHEDULE_READ: 'schedule.read',
  SCHEDULE_MANAGE_SELF: 'schedule.manage_self',
  SCHEDULE_MANAGE_ALL: 'schedule.manage_all',
  SERVICES_MANAGE_SELF: 'services.manage_self',
  MEMBER_SERVICES_READ_OWN: 'member_services.read_own',
  MEMBER_SERVICES_MANAGE_OWN: 'member_services.manage_own',
  MEMBER_SERVICES_READ_ALL: 'member_services.read_all',
  MEMBER_SERVICES_MANAGE_ALL: 'member_services.manage_all',
  MESSAGES_READ: 'messages.read',
  MESSAGES_SEND: 'messages.send',
  MESSAGES_MANAGE: 'messages.manage',
  GALLERY_READ: 'gallery.read',
  GALLERY_UPLOAD: 'gallery.upload',
  GALLERY_MANAGE: 'gallery.manage',
  BILLING_READ: 'billing.read',
  BILLING_MANAGE: 'billing.manage',
  BILLING_PAYOUT_MANAGE: 'billing.payout.manage',
  REVIEWS_READ: 'reviews.read',
  REVIEWS_RESPOND: 'reviews.respond',
  REVIEWS_MODERATE: 'reviews.moderate',
  AUDIT_READ: 'audit.read',
} as const;

export type ShopPermission = (typeof SHOP_PERMISSIONS)[keyof typeof SHOP_PERMISSIONS];
export type ShopRole = 'owner' | 'admin' | 'manager' | 'member' | 'receptionist';

/**
 * Roles that each shop role may assign through ordinary membership workflows.
 * Ownership changes use a separate transfer contract and are never an owner-add operation.
 */
export const SHOP_ROLE_ASSIGNMENTS: Readonly<Record<ShopRole, readonly ShopRole[]>> = {
  owner: ['admin', 'manager', 'member', 'receptionist'],
  admin: ['manager', 'member', 'receptionist'],
  manager: ['member', 'receptionist'],
  member: [],
  receptionist: [],
};

export function canAssignShopRole(actorRole: string, targetRole: ShopRole): boolean {
  return SHOP_ROLE_ASSIGNMENTS[actorRole as ShopRole]?.includes(targetRole) ?? false;
}

export interface ShopPermissionGroup {
  id: string;
  label: string;
  permissions: readonly ShopPermission[];
}

export const SHOP_PERMISSION_GROUPS: readonly ShopPermissionGroup[] = [
  {
    id: 'appointments',
    label: 'Appointments',
    permissions: [
      SHOP_PERMISSIONS.APPOINTMENTS_READ,
      SHOP_PERMISSIONS.APPOINTMENTS_READ_ALL,
      SHOP_PERMISSIONS.APPOINTMENTS_CREATE,
      SHOP_PERMISSIONS.APPOINTMENTS_UPDATE,
      SHOP_PERMISSIONS.APPOINTMENTS_CHECK_IN,
      SHOP_PERMISSIONS.APPOINTMENTS_CANCEL,
    ],
  },
  {
    id: 'clients',
    label: 'Clients',
    permissions: [
      SHOP_PERMISSIONS.CLIENTS_READ,
      SHOP_PERMISSIONS.CLIENTS_READ_ALL,
      SHOP_PERMISSIONS.CLIENTS_CREATE,
      SHOP_PERMISSIONS.CLIENTS_UPDATE,
      SHOP_PERMISSIONS.CLIENTS_CONTACT_MANAGE,
    ],
  },
  {
    id: 'members',
    label: 'Team',
    permissions: [
      SHOP_PERMISSIONS.MEMBERS_READ,
      SHOP_PERMISSIONS.MEMBERS_INVITE,
      SHOP_PERMISSIONS.MEMBERS_UPDATE,
      SHOP_PERMISSIONS.MEMBERS_REMOVE,
      SHOP_PERMISSIONS.MEMBERS_ROLE_MANAGE,
      SHOP_PERMISSIONS.MEMBERS_PERMISSIONS_MANAGE,
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    permissions: [
      SHOP_PERMISSIONS.SCHEDULE_READ,
      SHOP_PERMISSIONS.SCHEDULE_MANAGE_ALL,
      SHOP_PERMISSIONS.SERVICES_READ,
      SHOP_PERMISSIONS.SERVICES_CREATE,
      SHOP_PERMISSIONS.SERVICES_UPDATE,
      SHOP_PERMISSIONS.PAYMENTS_READ,
      SHOP_PERMISSIONS.PAYMENTS_CREATE,
      SHOP_PERMISSIONS.PAYMENTS_REFUND,
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    permissions: [
      SHOP_PERMISSIONS.REPORTS_VIEW,
      SHOP_PERMISSIONS.AUDIT_READ,
      SHOP_PERMISSIONS.SHOP_SETTINGS_MANAGE,
      SHOP_PERMISSIONS.SHOP_BILLING_MANAGE,
    ],
  },
];

export interface PermissionDiff {
  added: ShopPermission[];
  removed: ShopPermission[];
}

export function diffShopPermissions(
  before: ReadonlySet<ShopPermission>,
  after: ReadonlySet<ShopPermission>,
): PermissionDiff {
  return {
    added: [...after].filter((permission) => !before.has(permission)),
    removed: [...before].filter((permission) => !after.has(permission)),
  };
}

export interface PermissionOverrides {
  allow: ShopPermission[];
  deny: ShopPermission[];
}

export const NON_DELEGABLE_SHOP_PERMISSIONS = new Set<ShopPermission>([
  SHOP_PERMISSIONS.SHOP_OWNERSHIP_TRANSFER,
  SHOP_PERMISSIONS.SHOP_DELETE,
  SHOP_PERMISSIONS.SHOP_ARCHIVE,
  SHOP_PERMISSIONS.SHOP_ADMINS_MANAGE,
  SHOP_PERMISSIONS.SHOP_BILLING_MANAGE,
]);

export const OWNER_PROTECTED_SHOP_PERMISSIONS = new Set<ShopPermission>([
  SHOP_PERMISSIONS.MEMBERS_PERMISSIONS_MANAGE,
  SHOP_PERMISSIONS.MEMBERS_ROLE_MANAGE,
  SHOP_PERMISSIONS.SHOP_BILLING_MANAGE,
  SHOP_PERMISSIONS.BILLING_MANAGE,
  SHOP_PERMISSIONS.SHOP_SETTINGS_MANAGE,
]);

export const SHOP_PERMISSION_DEPENDENCIES: ReadonlyMap<ShopPermission, readonly ShopPermission[]> =
  new Map([
    [SHOP_PERMISSIONS.APPOINTMENTS_UPDATE, [SHOP_PERMISSIONS.APPOINTMENTS_READ]],
    [SHOP_PERMISSIONS.APPOINTMENTS_CHECK_IN, [SHOP_PERMISSIONS.APPOINTMENTS_READ]],
    [SHOP_PERMISSIONS.APPOINTMENTS_CANCEL, [SHOP_PERMISSIONS.APPOINTMENTS_READ]],
    [SHOP_PERMISSIONS.PAYMENTS_REFUND, [SHOP_PERMISSIONS.PAYMENTS_READ]],
    [SHOP_PERMISSIONS.MEMBERS_PERMISSIONS_MANAGE, [SHOP_PERMISSIONS.MEMBERS_READ]],
    [SHOP_PERMISSIONS.MEMBERS_ROLE_MANAGE, [SHOP_PERMISSIONS.MEMBERS_READ]],
    [SHOP_PERMISSIONS.MEMBERS_REMOVE, [SHOP_PERMISSIONS.MEMBERS_READ]],
    [SHOP_PERMISSIONS.MEMBERS_UPDATE, [SHOP_PERMISSIONS.MEMBERS_READ]],
  ]);

export interface ShopAuthorization {
  uid: string;
  shopId: string;
  role: ShopRole;
  authorizationVersion?: number;
  permissions: ReadonlySet<ShopPermission>;
}

export interface ShopPermissionMembership {
  uid?: string;
  shopId?: string;
  role: ShopRole;
  status: string;
  permissionOverrides?: PermissionOverrides;
  permissions?: ReadonlySet<ShopPermission>;
}

export interface PermissionContext {
  shopId: string;
  actorUserId: string;
  actorMemberId?: string;
  resourceType?: string;
  resourceOwnerMemberId?: string;
  resourceId?: string;
  resourceShopId?: string;
}

export interface ShopAuthorizationResult {
  allowed: boolean;
  reason?:
    | 'MEMBERSHIP_INACTIVE'
    | 'MEMBERSHIP_CONTEXT_MISMATCH'
    | 'RESOURCE_TENANT_MISMATCH'
    | 'PERMISSION_DENIED'
    | 'RESOURCE_SCOPE_DENIED';
}

export const SHOP_ROLE_PERMISSIONS: Record<ShopRole, ReadonlySet<ShopPermission>> = {
  owner: new Set<ShopPermission>(Object.values(SHOP_PERMISSIONS)),
  admin: new Set([
    SHOP_PERMISSIONS.SHOP_READ,
    SHOP_PERMISSIONS.SHOP_UPDATE,
    SHOP_PERMISSIONS.SHOP_ONBOARDING_COMPLETE,
    SHOP_PERMISSIONS.SHOP_PROFILE_SLUG_MANAGE,
    SHOP_PERMISSIONS.SHOP_MEMBERS_READ,
    SHOP_PERMISSIONS.SHOP_MEMBERS_ADD,
    SHOP_PERMISSIONS.SHOP_MEMBERS_MANAGE,
    SHOP_PERMISSIONS.SHOP_SETTINGS_MANAGE,
    SHOP_PERMISSIONS.SHOP_NOTIFICATION_SETTINGS_READ,
    SHOP_PERMISSIONS.SHOP_NOTIFICATION_SETTINGS_MANAGE,
    SHOP_PERMISSIONS.SHOP_MEMBER_SETTINGS_READ,
    SHOP_PERMISSIONS.SHOP_MEMBER_SETTINGS_MANAGE,
    SHOP_PERMISSIONS.SHOP_CLIENT_SETTINGS_MANAGE,
    SHOP_PERMISSIONS.SCHEDULE_CLOSURES_MANAGE,
    SHOP_PERMISSIONS.SCHEDULE_READ,
    SHOP_PERMISSIONS.SCHEDULE_MANAGE_ALL,
    SHOP_PERMISSIONS.APPOINTMENT_SETTINGS_MANAGE,
    SHOP_PERMISSIONS.MEMBERS_READ,
    SHOP_PERMISSIONS.MEMBERS_INVITE,
    SHOP_PERMISSIONS.MEMBERS_ADD,
    SHOP_PERMISSIONS.MEMBERS_MANAGE,
    SHOP_PERMISSIONS.MEMBERS_UPDATE,
    SHOP_PERMISSIONS.MEMBERS_REMOVE,
    SHOP_PERMISSIONS.MEMBERS_ROLE_MANAGE,
    SHOP_PERMISSIONS.MEMBERS_PERMISSIONS_MANAGE,
    SHOP_PERMISSIONS.MEMBERS_STATUS_MANAGE,
    SHOP_PERMISSIONS.MEMBER_SERVICES_READ_ALL,
    SHOP_PERMISSIONS.MEMBER_SERVICES_MANAGE_ALL,
    SHOP_PERMISSIONS.APPOINTMENTS_READ,
    SHOP_PERMISSIONS.APPOINTMENTS_CREATE,
    SHOP_PERMISSIONS.APPOINTMENTS_UPDATE,
    SHOP_PERMISSIONS.APPOINTMENTS_CHECK_IN,
    SHOP_PERMISSIONS.APPOINTMENTS_CANCEL,
    SHOP_PERMISSIONS.SERVICES_READ,
    SHOP_PERMISSIONS.SCHEDULE_READ,
    SHOP_PERMISSIONS.MEMBER_SERVICES_READ_ALL,
    SHOP_PERMISSIONS.MEMBER_SERVICES_MANAGE_ALL,
    SHOP_PERMISSIONS.SERVICES_CREATE,
    SHOP_PERMISSIONS.SERVICES_UPDATE,
    SHOP_PERMISSIONS.SERVICES_MANAGE,
    SHOP_PERMISSIONS.CLIENTS_READ,
    SHOP_PERMISSIONS.CLIENTS_READ_ALL,
    SHOP_PERMISSIONS.CLIENTS_CREATE,
    SHOP_PERMISSIONS.CLIENTS_UPDATE,
    SHOP_PERMISSIONS.CLIENTS_MERGE,
    SHOP_PERMISSIONS.CLIENTS_CONTACT_READ,
    SHOP_PERMISSIONS.CLIENTS_CONTACT_MANAGE,
    SHOP_PERMISSIONS.CLIENTS_NOTES_READ,
    SHOP_PERMISSIONS.CLIENTS_NOTES_MANAGE,
    SHOP_PERMISSIONS.REPORTS_VIEW,
  ]),
  manager: new Set([
    SHOP_PERMISSIONS.SHOP_READ,
    SHOP_PERMISSIONS.SHOP_MEMBERS_READ,
    SHOP_PERMISSIONS.SHOP_NOTIFICATION_SETTINGS_READ,
    SHOP_PERMISSIONS.APPOINTMENTS_READ,
    SHOP_PERMISSIONS.APPOINTMENTS_CREATE,
    SHOP_PERMISSIONS.APPOINTMENTS_UPDATE,
    SHOP_PERMISSIONS.APPOINTMENTS_CHECK_IN,
    SHOP_PERMISSIONS.APPOINTMENTS_CANCEL,
    SHOP_PERMISSIONS.CLIENTS_READ,
    SHOP_PERMISSIONS.CLIENTS_READ_ALL,
    SHOP_PERMISSIONS.CLIENTS_CREATE,
    SHOP_PERMISSIONS.CLIENTS_UPDATE,
    SHOP_PERMISSIONS.CLIENTS_CONTACT_READ,
    SHOP_PERMISSIONS.CLIENTS_NOTES_READ,
    SHOP_PERMISSIONS.CLIENTS_NOTES_MANAGE,
    SHOP_PERMISSIONS.SERVICES_READ,
    SHOP_PERMISSIONS.MEMBER_SERVICES_READ_OWN,
    SHOP_PERMISSIONS.MEMBER_SERVICES_MANAGE_OWN,
    SHOP_PERMISSIONS.REPORTS_VIEW,
  ]),
  member: new Set([
    SHOP_PERMISSIONS.SHOP_READ,
    SHOP_PERMISSIONS.APPOINTMENTS_READ_OWN,
    SHOP_PERMISSIONS.APPOINTMENTS_MANAGE_OWN,
    SHOP_PERMISSIONS.SCHEDULE_READ,
    SHOP_PERMISSIONS.SCHEDULE_MANAGE_SELF,
    SHOP_PERMISSIONS.SERVICES_READ,
    SHOP_PERMISSIONS.MEMBER_SERVICES_READ_OWN,
    SHOP_PERMISSIONS.MEMBER_SERVICES_MANAGE_OWN,
  ]),
  receptionist: new Set([
    SHOP_PERMISSIONS.SHOP_READ,
    SHOP_PERMISSIONS.APPOINTMENTS_READ,
    SHOP_PERMISSIONS.APPOINTMENTS_CREATE,
    SHOP_PERMISSIONS.APPOINTMENTS_UPDATE,
    SHOP_PERMISSIONS.APPOINTMENTS_CANCEL,
    SHOP_PERMISSIONS.CLIENTS_READ,
    SHOP_PERMISSIONS.CLIENTS_CREATE,
    SHOP_PERMISSIONS.CLIENTS_UPDATE,
    SHOP_PERMISSIONS.CLIENTS_CONTACT_READ,
    SHOP_PERMISSIONS.SERVICES_READ,
    SHOP_PERMISSIONS.PAYMENTS_READ,
    SHOP_PERMISSIONS.PAYMENTS_CREATE,
  ]),
};

export function resolveEffectiveShopPermissions(
  role: ShopRole,
  overrides?: PermissionOverrides,
): ReadonlySet<ShopPermission> {
  const effective = new Set(SHOP_ROLE_PERMISSIONS[role]);
  for (const permission of overrides?.allow ?? []) effective.add(permission);
  for (const permission of effective) {
    for (const dependency of SHOP_PERMISSION_DEPENDENCIES.get(permission) ?? []) {
      effective.add(dependency);
    }
  }
  for (const permission of overrides?.deny ?? []) effective.delete(permission);
  if (role === 'owner') {
    for (const permission of OWNER_PROTECTED_SHOP_PERMISSIONS) effective.add(permission);
  }
  return effective;
}

export function resolveShopPermissions(
  membership: ShopPermissionMembership,
): ReadonlySet<ShopPermission> {
  if (membership.status !== 'active') return new Set();
  return resolveEffectiveShopPermissions(membership.role, membership.permissionOverrides);
}

export function hasShopPermission(
  membership: ShopPermissionMembership,
  permission: ShopPermission,
): boolean {
  if (membership.status !== 'active') return false;
  return (membership.permissions ?? resolveShopPermissions(membership)).has(permission);
}

export function canAccessResource(
  permission: ShopPermission,
  membership: ShopPermissionMembership,
  context: PermissionContext,
): ShopAuthorizationResult {
  if (membership.status !== 'active') {
    return { allowed: false, reason: 'MEMBERSHIP_INACTIVE' };
  }
  if (
    !membership.uid ||
    !membership.shopId ||
    membership.uid !== context.actorUserId ||
    membership.shopId !== context.shopId
  ) {
    return { allowed: false, reason: 'MEMBERSHIP_CONTEXT_MISMATCH' };
  }
  if (context.resourceShopId !== undefined && context.resourceShopId !== context.shopId) {
    return { allowed: false, reason: 'RESOURCE_TENANT_MISMATCH' };
  }

  const permissions = membership.permissions ?? resolveShopPermissions(membership);
  const ownsResource =
    context.actorMemberId !== undefined &&
    context.resourceOwnerMemberId !== undefined &&
    context.actorMemberId === context.resourceOwnerMemberId;
  const isOwnScopedPermission = permission.endsWith('_own') || permission.endsWith('_self');

  if (isOwnScopedPermission) {
    if (!permissions.has(permission)) return { allowed: false, reason: 'PERMISSION_DENIED' };
    return ownsResource ? { allowed: true } : { allowed: false, reason: 'RESOURCE_SCOPE_DENIED' };
  }

  if (permissions.has(permission)) return { allowed: true };

  const allPermission = `${permission}_all` as ShopPermission;
  if (permissions.has(allPermission)) return { allowed: true };

  const ownPermission = `${permission}_own` as ShopPermission;
  const selfPermission = `${permission}_self` as ShopPermission;
  if (ownsResource && (permissions.has(ownPermission) || permissions.has(selfPermission))) {
    return { allowed: true };
  }

  if (permissions.has(ownPermission) || permissions.has(selfPermission)) {
    return { allowed: false, reason: 'RESOURCE_SCOPE_DENIED' };
  }
  return { allowed: false, reason: 'PERMISSION_DENIED' };
}

export function normalizePermissionOverrides(
  allow: readonly ShopPermission[] = [],
  deny: readonly ShopPermission[] = [],
): PermissionOverrides {
  const normalizedAllow = new Set(allow);
  for (const permission of normalizedAllow) {
    for (const dependency of SHOP_PERMISSION_DEPENDENCIES.get(permission) ?? []) {
      normalizedAllow.add(dependency);
    }
  }
  const normalizedDeny = [...new Set(deny)].filter(
    (permission) => !OWNER_PROTECTED_SHOP_PERMISSIONS.has(permission),
  );
  const denied = new Set(normalizedDeny);
  for (const permission of normalizedAllow) {
    for (const dependency of SHOP_PERMISSION_DEPENDENCIES.get(permission) ?? []) {
      denied.delete(dependency);
    }
  }
  return {
    allow: [...normalizedAllow].filter((permission) => !denied.has(permission)),
    deny: [...denied].filter((permission) => !normalizedAllow.has(permission)),
  };
}

export function isShopPermission(value: unknown): value is ShopPermission {
  return typeof value === 'string' && (Object.values(SHOP_PERMISSIONS) as string[]).includes(value);
}
